import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';

const EditConsumableModal = ({ isOpen, onClose, consumable, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'piece',
    minimum_threshold: 10,
    unit_price: '',
    supplier_id: '',
    reference_code: '',
    is_active: true
  });

  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadSuppliers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (consumable) {
      setFormData({
        name: consumable?.name || '',
        description: consumable?.description || '',
        unit: consumable?.unit || 'piece',
        minimum_threshold: consumable?.minimum_threshold || 10,
        unit_price: consumable?.unit_price || '',
        supplier_id: consumable?.supplier_id || '',
        reference_code: consumable?.reference_code || '',
        is_active: consumable?.is_active !== false
      });
    }
  }, [consumable]);

  const loadSuppliers = async () => {
    try {
      const { data, error } = await supabase?.from('suppliers')?.select('id, name')?.eq('is_active', true)?.order('name');
      if (error) throw error;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };

  const unitOptions = [
    { value: 'piece', label: 'Pièce' },
    { value: 'meter', label: 'Mètre' },
    { value: 'liter', label: 'Litre' },
    { value: 'kilogram', label: 'Kilogramme' },
    { value: 'box', label: 'Boîte' },
    { value: 'roll', label: 'Rouleau' },
    { value: 'tube', label: 'Tube' },
    { value: 'cartridge', label: 'Cartouche' },
    { value: 'bottle', label: 'Bouteille' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(consumable?.id, {
      ...formData,
      minimum_threshold: parseInt(formData?.minimum_threshold) || 10,
      unit_price: formData?.unit_price ? parseFloat(formData?.unit_price) : null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Edit" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Modifier le consommable</h3>
                <p className="text-sm text-muted-foreground">Stock actuel: {consumable?.current_stock}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom du consommable"
              type="text"
              placeholder="Ex: Joint en caoutchouc 20mm"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              required
            />

            <Input
              label="Code référence"
              type="text"
              placeholder="Ex: JNT-20MM"
              value={formData?.reference_code}
              onChange={(e) => setFormData({ ...formData, reference_code: e?.target?.value })}
            />
          </div>

          <Input
            label="Description"
            type="text"
            placeholder="Description détaillée du consommable"
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Unité de mesure"
              options={unitOptions}
              value={formData?.unit}
              onChange={(value) => setFormData({ ...formData, unit: value })}
              required
            />

            <Input
              label="Seuil minimum"
              type="number"
              placeholder="10"
              value={formData?.minimum_threshold}
              onChange={(e) => setFormData({ ...formData, minimum_threshold: e?.target?.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prix unitaire (€)"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e?.target?.value })}
            />

            <Select
              label="Fournisseur"
              options={suppliers?.map((s) => ({ value: s?.id, label: s?.name }))}
              value={formData?.supplier_id}
              onChange={(value) => setFormData({ ...formData, supplier_id: value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="is_active"
              checked={formData?.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <label htmlFor="is_active" className="text-sm text-foreground cursor-pointer">
              Consommable actif
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              className="flex-1"
            >
              Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditConsumableModal;